import { CalendarRange, ChevronLeft, ChevronRight, ClipboardCheck, Printer } from 'lucide-react';
import { useCallback, useMemo, useState, type ReactNode } from 'react';
import { toast } from 'sonner';
import { useLocation } from 'react-router-dom';
import { PERMISSION } from '@obraplan/core/domain/access/permissions';
import type { InspectionStatus } from '@obraplan/core/domain/planning/daily-inspection';
import { teamWorkerName } from '@obraplan/core/domain/planning/daily-report-task';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { EmptyState, ErrorState, LoadingState } from '@/components/feedback/states';
import { AttachmentGallery } from '@/features/planning/AttachmentGallery';
import { DailyReportPanel } from '@/features/planning/DailyReportPanel';
import type { TrocaDeAtividade } from '@/features/planning/DailyReportWork';
import { DayMeasurementDialog } from '@/features/planning/DayMeasurementDialog';
import { TeamMeasurementDialog } from '@/features/planning/TeamMeasurementDialog';
import { TeamMeasurementPanel } from '@/features/planning/TeamMeasurementPanel';
import type { CarregarPecas } from '@/features/planning/PecasDoDia';
import type {
  PendingNonConformityPhotos,
  VerificationInput,
} from '@/features/planning/quality-verification-draft';
import { mensagemDasFotos } from '@/features/planning/subir-fotos-das-ncs';
import { semResposta } from '@/app/conference-operations';
import {
  equipeNaFila,
  PENDING_INSPECTION_ID,
  type ConferenceOperation,
  type SubmitOutcome,
} from '@/app/outbox-types';
import {
  comAFila,
  filaPorPessoa,
  GUARDADO_NO_APARELHO,
  syncDoCartao,
} from '@/features/planning/fila-da-conferencia';
import { horasLancadasNaFila } from '@/features/planning/horas-da-fila';
import {
  equipesComAFila,
  PENDING_TEAM_MEASUREMENT_ID,
  syncDaFrente,
} from '@/features/planning/fila-da-equipe-e-do-rdo';
import { useFilaPorCima } from '@/features/planning/use-fila-por-cima';
import {
  estaNaFila,
  galeriaComAFila,
  removerAFoto,
  urlDaFotoNaFila,
} from '@/features/planning/fotos-na-fila';
import { InspectionCard, type FrontQualityBadge } from '@/features/planning/InspectionCard';
import { InspectionReport } from '@/features/planning/InspectionReport';
import { AbsenceDialog, ChangeHistoryDialog } from '@/features/planning/InspectionDialogs';
import { ProjectVersionNotice } from '@/features/projects/ProjectVersionNotice';
import { useContainer, usePermission, useSessionState } from '@/features/session/use-session';
import { useCommand } from '@/lib/use-command';
import { useLive } from '@/lib/use-live';
import { ESGOTADO, noPrazo } from '@/lib/no-prazo';
import { cn } from '@/lib/cn';
import { NcPlacesContext } from '@/features/planning/nc-places';
import { useNcPlacesTools } from '@/features/planning/use-nc-places-tools';
import { DIAS_ANTERIORES_NO_APARELHO } from '@/features/offline/use-preload-today';

/**
 * Conferência diária — o segundo par de olhos (Q12 a Q16). Feita para tablet: cartões
 * grandes, confirmar num toque. Ela avisa que a justificativa será obrigatória quando o
 * total do dia passar da jornada (RN-292), mas quem recusa é o caso de uso (RN-134).
 *
 * ## A mesma rota guarda dois recortes do mesmo dia (Fase 18, fatia 6)
 *
 * A conferência é **por pessoa**: quatorze cartões num dia de quatorze funcionários. O
 * RDO é **por obra e por dia**: um documento, um clima, uma observação. São o mesmo dia
 * visto de duas alturas, e por isso dividem a barra de data, a obra e a rota — o RDO não
 * ganhou tela nova nem entrada de menu (ADR-121).
 *
 * Dividem a rota, e não a rolagem. A tentação era empilhar as seções do documento embaixo
 * dos cartões, e ela custa caro no tablet: quem confere quatorze pessoas ao longo do dia
 * passaria por sete seções de formulário a cada vez, e quem preenche o documento uma vez
 * às 17h rolaria por quatorze cartões para chegar ao clima. As abas separam os dois
 * momentos sem separar o dia.
 */

type Filter = 'ALL' | InspectionStatus;

/** O aviso da gravação que subiu na hora, ou do que ficou no aparelho. */
function mensagemDoMedir(resultado: SubmitOutcome): string {
  if (resultado.queued) return GUARDADO_NO_APARELHO;
  const { outcome } = resultado;
  const partes = [
    ...(outcome.confirmed ? ['Conferência confirmada'] : []),
    ...(outcome.adjusted > 0
      ? [
          outcome.adjusted === 1
            ? 'Conferência ajustada · horas de 1 frente'
            : `Conferência ajustada · horas de ${String(outcome.adjusted)} frentes`,
        ]
      : []),
    ...(outcome.pieces > 0 ? ['Peças marcadas'] : []),
    ...(outcome.measured > 0 ? ['Medição do dia registrada'] : []),
    ...(outcome.measured === 0 && outcome.verified > 0 ? ['Verificação registrada'] : []),
    ...(outcome.nonConformities > 0
      ? [`${String(outcome.nonConformities)} não conformidade(s) aberta(s)`]
      : []),
    ...mensagemDasFotos({ enviadas: outcome.photos.sent, falhas: outcome.photos.failures }),
  ];
  return partes.length === 0 ? 'Nada mudou neste dia.' : `${partes.join(' · ')}.`;
}

const FILTERS: readonly { id: Filter; label: string }[] = [
  { id: 'ALL', label: 'Todos' },
  { id: 'PENDING', label: 'Pendentes' },
  { id: 'CONFIRMED', label: 'Confirmados' },
  { id: 'ADJUSTED', label: 'Ajustados' },
  { id: 'ABSENT', label: 'Ausentes' },
];

/** Quanto abrir a caixa de quem ainda não tem conferência espera a rede, no sinal fraco (RN-619). */
const PRAZO_PARA_ABRIR_MS = 3000;

export function InspectionPage() {
  const container = useContainer();
  const { session, activeProjectId } = useSessionState();
  const canConfirm = usePermission(PERMISSION.LABOR_CONFIRM);
  const canModify = usePermission(PERMISSION.LABOR_MODIFY);

  // O dia escolhido vale para a entrada atual da página: clicar "Conferência" no menu é uma entrada
  // nova, e volta para hoje — também sem sinal, no dia que não estava guardado (RN-638).
  const { key: entrada } = useLocation();
  const [escolhido, setEscolhido] = useState(() => ({
    entrada,
    date: container.clock.today('America/Sao_Paulo'),
  }));
  const date =
    escolhido.entrada === entrada ? escolhido.date : container.clock.today('America/Sao_Paulo');
  const setDate = (dia: typeof date) => setEscolhido({ entrada, date: dia });
  const ncPlaces = useNcPlacesTools(activeProjectId, date.toString());
  const [filter, setFilter] = useState<Filter>('ALL');
  const [absent, setAbsent] = useState<
    { resourceId: string; resourceName: string; planned: number } | undefined
  >(undefined);
  const [history, setHistory] = useState<{ resourceId: string; resourceName: string } | undefined>(
    undefined,
  );
  // A medição do dia aberta. Nome e frentes saem de `lines` no render — guardar cópia aqui
  // desatualizaria a caixa quando a lista mudasse embaixo dela.
  const [measuring, setMeasuring] = useState<
    { inspectionId: string; resourceId: string } | undefined
  >(undefined);
  // A descrição da próxima foto da conferência (Fase 20), como no relatório fotográfico do
  // RDO. Mora aqui, e não na galeria, porque é quem envia o arquivo que a manda junto.
  const [legendaFoto, setLegendaFoto] = useState('');
  // A medição da equipe aberta (Fase 30): só a chave — a frente sai da leitura viva no render,
  // e é assim que a galeria aparece logo depois da primeira gravação.
  const [measuringTeam, setMeasuringTeam] = useState<
    { teamId: string; activityId: string; serviceId: string } | undefined
  >(undefined);
  const [legendaFotoEquipe, setLegendaFotoEquipe] = useState('');
  // A atividade lançada errada, à espera da certa no "Lançar trabalho" (RN-645). Leva o dia: o
  // outro dia da barra não herda a troca.
  const [troca, setTroca] = useState<(TrocaDeAtividade & { date: string }) | undefined>(undefined);
  // A fila do aparelho neste dia (Fase 43, RN-604): o cartão mostra o efeito esperado por cima.
  const fila = useLive(
    () =>
      session === undefined || activeProjectId === undefined
        ? Promise.resolve([])
        : container.outbox.list(session.userId, {
            projectId: activeProjectId,
            date: date.toString(),
          }),
    [session?.userId, activeProjectId, date.toString()],
  );
  /** Grava agora, ou guarda no aparelho quando não há sinal (RN-603). */
  const submeter = useCallback(
    (operacao: ConferenceOperation) => container.outbox.submit(operacao, session!),
    [container, session],
  );
  const view = useLive(
    () =>
      session === undefined || activeProjectId === undefined
        ? Promise.resolve(undefined)
        : container.getDailyInspections.execute(
            { projectId: activeProjectId, date: date.toString() },
            session,
          ),
    [session?.userId, activeProjectId, date.toString()],
  );

  // O que subiu da fila continua por cima até a leitura do dia ser refeita (`useFilaPorCima`).
  const entradasDoDia = useFilaPorCima(fila, view);
  const filaDoDia = useMemo(() => filaPorPessoa(entradasDoDia), [entradasDoDia]);
  // As horas que os lançamentos da fila ainda vão criar: o ajuste do cartão as conta (RN-642).
  const lancadasNaFila = useMemo(() => horasLancadasNaFila(entradasDoDia), [entradasDoDia]);

  // A qualidade do dia: os serviços de cada frente, as fichas e o que já foi verificado.
  // Uma leitura por dia, como a lista; revalida junto com ela a cada gravação.
  const quality = useLive(
    () =>
      session === undefined || activeProjectId === undefined
        ? Promise.resolve(undefined)
        : container.getDayQuality.execute(
            { projectId: activeProjectId, date: date.toString() },
            session,
          ),
    [session?.userId, activeProjectId, date.toString()],
  );
  const qualityByActivity = useMemo(() => {
    const map = new Map<string, FrontQualityBadge>();
    if (quality === undefined || !quality.ok) return map;
    for (const front of quality.value.fronts) {
      const verificados = front.services.flatMap((service) =>
        service.verification === undefined ? [] : [service.verification.summary],
      );
      map.set(front.activityId, {
        hasServices: front.services.length > 0,
        verified: verificados.length > 0,
        approved: verificados.reduce((sum, item) => sum + item.approved, 0),
        rejected: verificados.reduce((sum, item) => sum + item.rejected, 0),
        total: verificados.reduce((sum, item) => sum + item.total, 0),
      });
    }
    return map;
  }, [quality]);

  // A medição por equipe do dia (Fase 30, RN-405/406). Na obra por pessoa volta sem equipes, e
  // a tela fica como sempre foi.
  const teamView = useLive(
    () =>
      session === undefined || activeProjectId === undefined
        ? Promise.resolve(undefined)
        : container.getDayTeamMeasurements.execute(
            { projectId: activeProjectId, date: date.toString() },
            session,
          ),
    [session?.userId, activeProjectId, date.toString()],
  );
  // A medição por equipe com a fila do aparelho por cima (Fase 43, etapa 3, RN-611).
  const entradasDaEquipe = useFilaPorCima(fila, teamView);
  const equipesDoDia = useMemo(
    () =>
      teamView !== undefined && teamView.ok && teamView.value.mode === 'POR_EQUIPE'
        ? equipesComAFila(teamView.value.teams, entradasDaEquipe)
        : undefined,
    [teamView, entradasDaEquipe],
  );
  // Quem está numa equipe com frente a medir: no cartão dessa pessoa a quantidade é da equipe.
  const equipeDe = useMemo(() => {
    const map = new Map<string, string>();
    for (const team of equipesDoDia ?? []) {
      for (const front of team.fronts) {
        for (const member of front.members) {
          map.set(member.resourceId, teamWorkerName(team.teamName));
        }
      }
    }
    return map;
  }, [equipesDoDia]);
  const equipeMedida =
    measuringTeam === undefined
      ? undefined
      : equipesDoDia?.find((team) => team.teamId === measuringTeam.teamId);
  const frenteMedida = equipeMedida?.fronts.find(
    (front) =>
      front.activityId === measuringTeam?.activityId && front.serviceId === measuringTeam.serviceId,
  );
  // A medição que só existe na fila ainda não tem id no servidor: a foto dela vai para a fila
  // atrás dela, pela frente, e a galeria só lê do servidor a que já tem id.
  const medicaoDaEquipeId =
    frenteMedida?.measurement?.id === PENDING_TEAM_MEASUREMENT_ID
      ? undefined
      : frenteMedida?.measurement?.id;
  const teamGallery = useLive(
    () =>
      session === undefined || medicaoDaEquipeId === undefined
        ? Promise.resolve(undefined)
        : container.listTeamMeasurementPhotos.execute(
            { teamMeasurementId: medicaoDaEquipeId },
            session,
          ),
    [session?.userId, medicaoDaEquipeId],
  );

  // As fotos das NCs do dia, todas de uma vez: poucas NCs por dia, uma leitura por NC.
  // Revalida junto com o resto a cada gravação — anexar uma foto redesenha a galeria.
  const ncIds =
    quality !== undefined && quality.ok
      ? quality.value.nonConformities.map((nc) => nc.id as string)
      : [];
  const ncPhotos = useLive(async () => {
    if (session === undefined || ncIds.length === 0) return undefined;
    const entradas = await Promise.all(
      ncIds.map(async (id) => {
        const lido = await container.listNonConformityPhotos.execute(
          { nonConformityId: id },
          session,
        );
        return [id, lido.ok ? lido.value : undefined] as const;
      }),
    );
    return new Map(entradas);
  }, [session?.userId, ncIds.join('|')]);

  const changes = useLive(
    () =>
      session === undefined || activeProjectId === undefined || history === undefined
        ? Promise.resolve(undefined)
        : container.getPlanningChangeHistory.execute(
            { projectId: activeProjectId, resourceId: history.resourceId, date: date.toString() },
            session,
          ),
    [session?.userId, activeProjectId, date.toString(), history?.resourceId],
  );

  // A caixa aberta sem sinal não tem a conferência: a fila a abre quando sobe. A partir daí, a
  // leitura do dia traz o id, e a galeria e as gravações seguintes passam a usá-lo (RN-622).
  const inspecaoDaCaixa =
    measuring === undefined
      ? undefined
      : measuring.inspectionId !== PENDING_INSPECTION_ID
        ? measuring.inspectionId
        : ((view?.ok === true
            ? view.value.lines.find((line) => line.resource.resourceId === measuring.resourceId)
                ?.inspection?.id
            : undefined) ?? PENDING_INSPECTION_ID);

  // A galeria é reativa como o resto da tela: anexar ou remover redesenha sozinho.
  const gallery = useLive(
    () =>
      session === undefined ||
      inspecaoDaCaixa === undefined ||
      inspecaoDaCaixa === PENDING_INSPECTION_ID
        ? Promise.resolve(undefined)
        : container.listInspectionAttachments.execute({ inspectionId: inspecaoDaCaixa }, session),
    [session?.userId, inspecaoDaCaixa],
  );

  // O binário só é lido aqui, um anexo por vez, e quem revoga a URL é a galeria. A foto que
  // ainda está na fila sai do próprio arquivo, sem rede (RN-622).
  const resolveAttachmentUrl = useCallback(
    async (attachmentId: string): Promise<string | undefined> => {
      if (estaNaFila(attachmentId)) return urlDaFotoNaFila(attachmentId, fila ?? []);
      if (session === undefined) return undefined;
      const result = await container.getAttachmentUrl.execute({ attachmentId }, session);
      return result.ok ? result.value : undefined;
    },
    [container, session, fila],
  );

  // As peças da frente × serviço no dia (Fase 27, etapa 6): a caixa da medição pergunta por
  // linha, e o serviço que não mede peça volta sem `pieceKind` — e nada se desenha.
  const carregarPecas = useCallback<CarregarPecas>(
    async ({ activityId, serviceId, date: dia }) => {
      if (session === undefined || activeProjectId === undefined) return undefined;
      const lido = await container.getFrontPieces.execute(
        { projectId: activeProjectId, activityId, serviceId, date: dia },
        session,
      );
      return lido.ok ? lido.value : undefined;
    },
    [container, session, activeProjectId],
  );

  // O nome da obra vai no papel: duas obras imprimindo o mesmo dia geram folhas idênticas
  // e indistinguíveis sem ele.
  const project = useLive(
    () =>
      session === undefined || activeProjectId === undefined
        ? Promise.resolve(undefined)
        : container.getProject.execute(activeProjectId, session),
    [session?.userId, activeProjectId],
  );

  // Só para pôr nome na autoria do histórico de alterações. O quadro de perfis pertence a
  // quem administra, e a conferência é operada por quem não administra — então a recusa é
  // esperada aqui, e não é erro de tela: sem a lista, a autoria aparece sem nome. Dizer
  // "perfil removido" nesse caso seria afirmar, com cara de certeza, o que não se sabe.
  const users = useLive(
    () =>
      session === undefined
        ? Promise.resolve(undefined)
        : container.listUsers.execute({ includeInactive: true }, session),
    [session?.userId],
  );
  // O dia com a fila do aparelho por cima (RN-604): o que foi feito sem sinal já aparece.
  const linhasDoDia = useMemo(
    () =>
      view === undefined || !view.ok
        ? []
        : view.value.lines
            .map((line) => comAFila(line, filaDoDia.get(line.resource.resourceId) ?? []))
            // Quem ficou sem frente pela fila sai do dia, como o servidor fará (RN-631).
            .filter(
              (line, indice) =>
                line.allocations.length > 0 || view.value.lines[indice]!.allocations.length === 0,
            ),
    [view, filaDoDia],
  );

  const userNameOf = useMemo(() => {
    if (users === undefined || !users.ok) return () => 'outro perfil';
    const byId = new Map(users.value.map((item) => [item.user.id as string, item.user.name]));
    return (userId: string) => byId.get(userId) ?? 'perfil removido';
  }, [users]);

  // O diálogo grava o dia da pessoa numa sequência: as horas ajustadas, todas as frentes
  // numa chamada só (RN-104, pela mesma porta da conferência — o dia se encerra uma vez, e
  // a hora extra é do total final), depois a medição e a checklist, e por fim o veredito.
  // Um passo recusado para a sequência ali, e a recusa aparece no diálogo com o que falta
  // ainda por gravar — nada fica pela metade sem que a pessoa veja.
  //
  // Confirmar só acontece **sem ajuste de horas**: ajustar já encerra a conferência como
  // ADJUSTED (RN-104), e pedir CONFIRMED depois seria pedir para encerrar o que acabou de
  // encerrar — a recusa seria correta e incompreensível.
  const [saveMeasurement, measurementState] = useCommand(
    (input: {
      inspectionId: string;
      measurements: readonly { allocationId: string; executedQuantity: number | null }[];
      verifications: readonly VerificationInput[];
      adjustments: readonly {
        allocationId: string;
        expectedVersion: number;
        quarterHours: number;
      }[];
      justification?: string;
      pieces: readonly {
        allocationId: string;
        activityId: string;
        serviceId: string;
        pieceNames: readonly string[];
        /** As do dia quando a lista abriu: a fila manda só a diferença (RN-615). */
        previousPieceNames?: readonly string[];
      }[];
      /** As fotos escolhidas nos itens reprovados, para subir depois de gravar (RN-479). */
      photos: readonly PendingNonConformityPhotos[];
      resourceId: string;
      confirm: boolean;
    }) =>
      // A sequência inteira — horas, peças, medição, fotos das NCs e veredito — é uma operação
      // só (`conference-operations.ts`): sobe na hora, ou fica no aparelho sem sinal (RN-603).
      submeter({
        kind: 'medir',
        projectId: activeProjectId!,
        date: date.toString(),
        ...input,
      }),
    {
      successMessage: mensagemDoMedir,
      silent: true,
      onSuccess: () => setMeasuring(undefined),
    },
  );

  const [registerAbsence, absenceState] = useCommand(
    (input: { resourceId: string; reason: string }) =>
      submeter({ kind: 'ausencia', projectId: activeProjectId!, date: date.toString(), ...input }),
    {
      successMessage: (resultado) =>
        resultado.queued ? GUARDADO_NO_APARELHO : 'Ausência registrada.',
      silent: true,
      onSuccess: () => setAbsent(undefined),
    },
  );

  const [reopen] = useCommand(
    (input: { inspectionId: string; resourceId: string }) =>
      submeter({ kind: 'reabrir', projectId: activeProjectId!, date: date.toString(), ...input }),
    {
      successMessage: (resultado) =>
        resultado.queued ? GUARDADO_NO_APARELHO : 'Conferência reaberta.',
    },
  );

  // Tirar a frente do dia (RN-631): a que não aconteceu sai das horas da pessoa. Sem frente
  // nenhuma, a pessoa sai do dia, e o diálogo fecha junto.
  const [removeFront, removeFrontState] = useCommand(
    (input: { allocationId: string; expectedVersion: number }) =>
      // Sem sinal, entra na fila do aparelho e o cartão já mostra a frente fora (RN-620).
      submeter({
        kind: 'tirar-frente',
        projectId: activeProjectId!,
        date: date.toString(),
        resourceId: measuring?.resourceId ?? '',
        ...input,
      }),
    {
      successMessage: (feito) => {
        if (feito.queued || feito.outcome.removedFront === undefined) return GUARDADO_NO_APARELHO;
        const resultado = feito.outcome.removedFront;
        return (
          [
            resultado.remainingFronts === 0
              ? 'Frente tirada · a pessoa saiu do dia'
              : 'Frente tirada',
            ...(resultado.cancelledNonConformities > 0
              ? [`${String(resultado.cancelledNonConformities)} não conformidade(s) cancelada(s)`]
              : []),
            ...(resultado.verificationRemoved ? ['verificação de qualidade retirada'] : []),
            ...(resultado.piecesUnmarked > 0
              ? [`${String(resultado.piecesUnmarked)} peça(s) desmarcada(s)`]
              : []),
            ...(resultado.roomsUndone > 0
              ? [`${String(resultado.roomsUndone)} cômodo(s) de volta ao de antes`]
              : []),
          ].join(' · ') + '.'
        );
      },
      onSuccess: (feito) => {
        // Na fila, a conta é a do cartão: a frente tirada era a última que a pessoa tinha.
        const restantes = feito.queued
          ? (linhasDoDia.find((line) => line.resource.resourceId === measuring?.resourceId)
              ?.allocations.length ?? 1) - 1
          : (feito.outcome.removedFront?.remainingFronts ?? 1);
        if (restantes <= 0) setMeasuring(undefined);
      },
    },
  );

  const [attachFiles, attachState] = useCommand(
    (input: {
      inspectionId: string;
      resourceId: string;
      files: readonly File[];
      caption: string;
    }) =>
      submeter({
        kind: 'fotos-da-conferencia',
        projectId: activeProjectId!,
        date: date.toString(),
        ...input,
      }),
    {
      silent: true,
      successMessage: (resultado) => (resultado.queued ? GUARDADO_NO_APARELHO : 'Foto anexada.'),
      onSuccess: () => setLegendaFoto(''),
    },
  );

  // Com sinal ou sem (RN-621, RN-622): a foto da fila sai do aparelho; a que já subiu sai pela
  // fila, na chave de quem está aberto — a pessoa, ou a frente da equipe.
  const [removeAttachment] = useCommand(
    (attachmentId: string) =>
      removerAFoto(container.outbox, session!, attachmentId, {
        projectId: activeProjectId!,
        date: date.toString(),
        resourceId:
          measuring?.resourceId ??
          (measuringTeam === undefined
            ? 'fotos'
            : equipeNaFila(
                measuringTeam.teamId,
                measuringTeam.activityId,
                measuringTeam.serviceId,
              )),
      }),
    {
      successMessage: (resultado) => (resultado.queued ? GUARDADO_NO_APARELHO : 'Foto removida.'),
      silent: true,
    },
  );

  const [attachNcPhotos, attachNcState] = useCommand(
    (input: { nonConformityId: string; files: readonly File[] }) =>
      submeter({
        kind: 'fotos-da-nc',
        projectId: activeProjectId!,
        date: date.toString(),
        // A foto da NC não é de uma pessoa: a fila a ordena pela própria NC.
        resourceId: measuring?.resourceId ?? `nc:${input.nonConformityId}`,
        ...input,
      }),
    {
      successMessage: (resultado) =>
        resultado.queued ? GUARDADO_NO_APARELHO : 'Foto da não conformidade anexada.',
    },
  );

  // A medição da equipe (RN-406): a quantidade e a checklist num envelope só. Gravar um número
  // deixa a caixa aberta — a foto (D7) só tem onde entrar depois que a medição existe, e é aí
  // que se fotografa. Tirar a medição fecha a caixa: não sobra o que mostrar.
  const [saveTeamMeasurement, teamMeasurementState] = useCommand(
    (input: {
      teamId: string;
      activityId: string;
      serviceId: string;
      quantity: number | null;
      verifications: readonly VerificationInput[];
      pieceNames?: readonly string[];
      previousPieceNames?: readonly string[];
      photos?: readonly PendingNonConformityPhotos[];
    }) =>
      // As peças, a quantidade com a checklist e as fotos das NCs: uma operação só, que sobe na
      // hora ou fica no aparelho sem sinal (`conference-operations.ts`, RN-611).
      submeter({
        kind: 'medir-equipe',
        projectId: activeProjectId!,
        date: date.toString(),
        resourceId: equipeNaFila(input.teamId, input.activityId, input.serviceId),
        ...input,
        photos: input.photos ?? [],
      }),
    {
      successMessage: (resultado) => {
        if (resultado.queued) return GUARDADO_NO_APARELHO;
        const { outcome } = resultado;
        const partes = [
          outcome.teamMeasurementId === null
            ? 'Medição da equipe removida'
            : 'Medição da equipe registrada',
          ...(outcome.nonConformities > 0
            ? [`${String(outcome.nonConformities)} não conformidade(s) aberta(s)`]
            : []),
          ...mensagemDasFotos({ enviadas: outcome.photos.sent, falhas: outcome.photos.failures }),
        ];
        return `${partes.join(' · ')}.`;
      },
      silent: true,
      onSuccess: (resultado) => {
        // Sem sinal a caixa fica aberta, como com sinal: a foto entra na fila atrás da medição, e
        // acha o id dela quando subir (RN-622). Tirar a medição fecha: não sobra o que mostrar.
        if (!resultado.queued && resultado.outcome.teamMeasurementId === null) {
          setMeasuringTeam(undefined);
        }
      },
    },
  );

  // A foto da medição que ainda está na fila vai sem o id dela, com a frente: na mesma chave, sobe
  // depois da medição e a acha pela frente (RN-622).
  const [attachTeamPhotos, attachTeamState] = useCommand(
    (input: {
      teamMeasurementId: string | undefined;
      teamId: string;
      activityId: string;
      serviceId: string;
      files: readonly File[];
      caption: string;
    }) => {
      const { teamMeasurementId, ...resto } = input;
      return submeter({
        kind: 'fotos-da-equipe',
        projectId: activeProjectId!,
        date: date.toString(),
        resourceId: equipeNaFila(input.teamId, input.activityId, input.serviceId),
        ...resto,
        ...(teamMeasurementId === undefined ? {} : { teamMeasurementId }),
      });
    },
    {
      silent: true,
      successMessage: (resultado) => (resultado.queued ? GUARDADO_NO_APARELHO : 'Foto anexada.'),
      onSuccess: () => setLegendaFotoEquipe(''),
    },
  );

  // Abre a galeria de um recurso. Criar a conferência aqui é honesto: é quando o segundo par
  // de olhos começou a olhar (RN-100). Se já existe, a chave natural devolve a mesma.
  //
  // Sem sinal (Fase 43), a caixa abre com o que o aparelho tem: a conferência que já existia,
  // ou nenhuma ainda — e a operação a abre quando subir. Medir e confirmar continuam possíveis.
  //
  // No sinal fraco (RN-619), a caixa não espera a rede: com a conferência já conhecida, abre na
  // hora; sem ela, espera no máximo `PRAZO_PARA_ABRIR_MS` e abre com a pendente do aparelho.
  const openMeasurement = async (line: (typeof linhasDoDia)[number]) => {
    if (session === undefined) return;
    const resourceId = line.resource.resourceId;
    const conhecida = line.inspection?.id;
    if (conhecida !== undefined && conhecida !== PENDING_INSPECTION_ID) {
      setMeasuring({ inspectionId: conhecida, resourceId });
      return;
    }
    const result = await noPrazo(
      container.openInspection.execute(
        { projectId: activeProjectId, resourceId, date: date.toString() },
        session,
      ),
      PRAZO_PARA_ABRIR_MS,
    );
    if (result === ESGOTADO) {
      setMeasuring({ inspectionId: PENDING_INSPECTION_ID, resourceId });
      return;
    }
    if (!result.ok) {
      if (semResposta(result.error)) {
        setMeasuring({ inspectionId: line.inspection?.id ?? PENDING_INSPECTION_ID, resourceId });
        return;
      }
      toast.error(result.error.message);
      return;
    }
    setMeasuring({ inspectionId: result.value.id, resourceId });
  };

  if (activeProjectId === undefined) {
    return (
      <EmptyState
        title="Nenhuma obra selecionada"
        description="Escolha uma obra no seletor do topo para conferir o dia."
        icon={<CalendarRange className="size-8" />}
      />
    );
  }

  if (view === undefined) return <LoadingState label="Carregando a conferência…" />;
  // Sem sinal, o dia que não está no aparelho não derruba a tela (RN-638): as setas continuam, e
  // quem foi um dia longe demais volta para os que a pré-carga guardou (RN-636).
  if (!view.ok && semResposta(view.error)) {
    const hoje = container.clock.today('America/Sao_Paulo');
    return (
      <div className="flex flex-col gap-6">
        <h1 className="text-2xl font-semibold">Conferência diária</h1>
        <BarraDoDia
          dateLabel={rotuloDoDia(date.toString())}
          isToday={date.toString() === hoje.toString()}
          onAnterior={() => setDate(date.subtractDays(1))}
          onProximo={() => setDate(date.addDays(1))}
          onHoje={() => setDate(hoje)}
        />
        <EmptyState
          title="Este dia não está guardado no aparelho"
          description={`Sem sinal, a Conferência abre hoje e os ${String(DIAS_ANTERIORES_NO_APARELHO)} dias anteriores, guardados quando havia sinal. Volte para um deles, ou abra este dia quando o sinal voltar.`}
          icon={<CalendarRange className="size-8" />}
        />
      </div>
    );
  }
  if (!view.ok) return <ErrorState message={view.error.message} code={view.error.code} />;

  const data = view.value;
  const isToday = data.today === data.date;
  // A galeria da pessoa com a fila por cima: a foto tirada sem sinal aparece, e a removida sai
  // (RN-621, RN-622). A fila crua, e não a "por cima": a foto que subiu sai daqui na hora.
  const galeriaDaPessoa =
    measuring === undefined
      ? undefined
      : galeriaComAFila(
          gallery !== undefined && gallery.ok ? gallery.value : undefined,
          fila ?? [],
          {
            kind: 'conferencia',
            resourceId: measuring.resourceId,
            date: date.toString(),
          },
        );
  const lines = linhasDoDia.filter((line) => {
    if (filter === 'ALL') return true;
    const status = line.inspection?.status ?? 'PENDING';
    return status === filter;
  });

  // Conta com a fila por cima: o que foi confirmado sem sinal já conta como conferido.
  const done = linhasDoDia.filter(
    (line) => (line.inspection?.status ?? 'PENDING') !== 'PENDING',
  ).length;

  return (
    // O passo de qualidade dos modais lê daqui os lugares do dia e o encerrar (Fase 37, etapa 3).
    <NcPlacesContext.Provider value={ncPlaces}>
      {/* Na impressão sobra só o relatório: a tela inteira é escondida de uma vez. */}
      <div className="flex flex-col gap-6 print:hidden">
        <ProjectVersionNotice />
        <header className="flex flex-wrap items-start justify-between gap-3">
          <div>
            {/* Uma página só (Fase 20): a conferência pessoa a pessoa mora dentro do RDO,
                na seção "Mão de obra do dia". O título continua sendo o do menu — é a
                palavra pela qual quem usa chega aqui todo dia. */}
            <h1 className="text-2xl font-semibold">Conferência diária</h1>
            <p className="text-(--color-content-muted)">
              O que <strong>aconteceu</strong>: confira quem trabalhou, lance o que ninguém
              planejou, meça e fotografe — e emita o RDO do dia.
            </p>
          </div>

          {/*
            A folha de conferência continua saindo pela caixa de impressão do navegador: ela
            é folha de trabalho, impressa na hora. O RDO não sai por aqui — ele é anexado em
            e-mail e arquivado com nome padronizado, e por isso é gerado (ADR-124).
          */}
          <Button
            variant="secondary"
            className="min-h-11 w-full sm:min-h-9 sm:w-auto"
            disabled={lines.length === 0}
            onClick={() => {
              globalThis.print();
            }}
          >
            <Printer aria-hidden="true" />
            Imprimir a conferência
          </Button>
        </header>

        {/* ── Navegação por dia ───────────────────────────────────────────────── */}
        <BarraDoDia
          dateLabel={data.dateLabel}
          isToday={isToday}
          onAnterior={() => setDate(date.subtractDays(1))}
          onProximo={() => setDate(date.addDays(1))}
          onHoje={() => setDate(container.clock.today('America/Sao_Paulo'))}
        >
          <Badge tone={done === data.summary.total && data.summary.total > 0 ? 'ok' : 'neutral'}>
            {done} de {data.summary.total} conferidos
          </Badge>

          {!data.canInspect && <Badge tone="warn">Dia futuro — ainda não se confere</Badge>}
        </BarraDoDia>

        {/* Uma página só (ADR-132): o RDO é a página, e a conferência pessoa a pessoa mora
            na primeira seção dele. Os cartões são montados aqui porque os diálogos deles —
            ajuste, ausência, histórico, medição — são desta página. */}
        <DailyReportPanel
          projectId={activeProjectId}
          date={date.toString()}
          dateLabel={data.dateLabel}
          lines={data.lines}
          troca={troca?.date === date.toString() ? troca : undefined}
          onEncerrarTroca={() => setTroca(undefined)}
          conference={
            <div className="flex flex-col gap-4">
              {/* ── A medição por equipe (Fase 30) ──────────────────────────────────── */}
              {/* Na obra por equipe a quantidade se lança aqui, uma vez por equipe × frente ×
                  serviço; os cartões embaixo seguem com as horas, a ausência e a hora extra
                  de cada pessoa — e com a quantidade de quem não tem equipe (RN-407). */}
              {equipesDoDia !== undefined && (
                <div className="flex flex-col gap-2">
                  <h3 className="text-base font-semibold">Medição por equipe</h3>
                  <TeamMeasurementPanel
                    teams={equipesDoDia}
                    editable={canConfirm}
                    canInspect={data.canInspect}
                    isPending={teamMeasurementState.isPending}
                    syncOf={(team, front) =>
                      syncDaFrente(entradasDaEquipe, team.teamId, front.activityId, front.serviceId)
                    }
                    onMeasure={(team, front) =>
                      setMeasuringTeam({
                        teamId: team.teamId,
                        activityId: front.activityId,
                        serviceId: front.serviceId,
                      })
                    }
                  />
                  <h3 className="mt-2 text-base font-semibold">Pessoas</h3>
                </div>
              )}

              {/* ── Filtros ─────────────────────────────────────────────────────────── */}
              {/* Uma faixa rolável no celular; no monitor, os cinco filtros cabem lado a lado. */}
              <div className="flex gap-1 overflow-x-auto pb-1 md:flex-wrap md:pb-0">
                {FILTERS.map((item) => (
                  <button
                    key={item.id}
                    type="button"
                    aria-pressed={filter === item.id}
                    onClick={() => setFilter(item.id)}
                    className={cn(
                      'min-h-11 shrink-0 rounded-md px-3 py-1.5 text-sm md:min-h-9',
                      filter === item.id
                        ? 'bg-(--color-surface-muted) font-medium text-(--color-content)'
                        : 'text-(--color-content-muted) hover:bg-(--color-surface-muted)',
                    )}
                  >
                    {item.label}
                  </button>
                ))}
              </div>

              {lines.length === 0 ? (
                <EmptyState
                  title={
                    data.summary.total === 0
                      ? 'Nada planejado para este dia'
                      : 'Nenhum recurso neste filtro'
                  }
                  description={
                    data.summary.total === 0
                      ? 'A conferência lista quem tinha trabalho previsto. O planejamento da semana é onde se lança.'
                      : 'Troque o filtro para ver os demais.'
                  }
                  icon={<ClipboardCheck className="size-8" />}
                />
              ) : (
                <div className="grid grid-cols-1 gap-3 lg:grid-cols-2">
                  {/* `grid-cols-1` no celular: a coluna implícita `auto` crescia além da seção
                      e empurrava todo cartão 40px para fora da tela. `minmax(0, 1fr)` o segura. */}
                  {lines.map((line) => (
                    <InspectionCard
                      key={line.resource.resourceId}
                      line={line}
                      canConfirm={canConfirm}
                      canModify={canModify}
                      disabled={!data.canInspect || measurementState.isPending}
                      quality={qualityByActivity}
                      onAbsence={() =>
                        setAbsent({
                          resourceId: line.resource.resourceId,
                          resourceName: line.resource.name,
                          planned: line.plannedQuarterHours,
                        })
                      }
                      onReopen={(inspectionId) =>
                        void reopen({ inspectionId, resourceId: line.resource.resourceId })
                      }
                      onOpen={() => void openMeasurement(line)}
                      {...(() => {
                        const sync = syncDoCartao(filaDoDia.get(line.resource.resourceId) ?? []);
                        return sync === undefined ? {} : { sync };
                      })()}
                    />
                  ))}
                </div>
              )}
            </div>
          }
        />

        <AbsenceDialog
          open={absent !== undefined}
          onOpenChange={(open) => {
            if (!open) setAbsent(undefined);
          }}
          resourceName={absent?.resourceName ?? ''}
          plannedQuarterHours={absent?.planned ?? 0}
          isPending={absenceState.isPending}
          {...(absenceState.error === undefined
            ? {}
            : { errorMessage: absenceState.error.message })}
          onConfirm={(reason) => {
            if (absent === undefined) return;
            void registerAbsence({ resourceId: absent.resourceId, reason });
          }}
        />

        <DayMeasurementDialog
          open={measuring !== undefined}
          onOpenChange={(open) => {
            if (!open) setMeasuring(undefined);
          }}
          line={linhasDoDia.find((item) => item.resource.resourceId === measuring?.resourceId)}
          queuedQuarterHours={
            measuring === undefined ? 0 : (lancadasNaFila.get(measuring.resourceId) ?? 0)
          }
          inspectionId={inspecaoDaCaixa}
          quantityOfTeam={measuring === undefined ? undefined : equipeDe.get(measuring.resourceId)}
          dateLabel={data.dateLabel}
          quality={quality !== undefined && quality.ok ? quality.value : undefined}
          nonConformityPhotos={{
            of: (id) =>
              galeriaComAFila(ncPhotos?.get(id), fila ?? [], { kind: 'nc', nonConformityId: id }),
            canAttach: canConfirm && data.canInspect,
            canRemove: canModify,
            isPending: attachNcState.isPending,
            resolveUrl: resolveAttachmentUrl,
            onAttach: (nonConformityId, files) => void attachNcPhotos({ nonConformityId, files }),
            onRemove: (attachmentId) => void removeAttachment(attachmentId),
          }}
          attachments={galeriaDaPessoa?.attachments ?? []}
          usage={galeriaDaPessoa?.usage}
          canAttach={canConfirm && data.canInspect}
          canRemove={canModify}
          isSaving={measurementState.isPending || removeFrontState.isPending}
          isAttaching={attachState.isPending}
          {...((measurementState.error ?? removeFrontState.error) === undefined
            ? {}
            : { errorMessage: (measurementState.error ?? removeFrontState.error)!.message })}
          {...(attachState.error === undefined
            ? {}
            : { attachErrorMessage: attachState.error.message })}
          resolveUrl={resolveAttachmentUrl}
          caption={{ value: legendaFoto, onChange: setLegendaFoto }}
          onAttach={(files) => {
            if (measuring !== undefined)
              void attachFiles({
                inspectionId: inspecaoDaCaixa ?? measuring.inspectionId,
                resourceId: measuring.resourceId,
                files,
                caption: legendaFoto,
              });
          }}
          onRemoveAttachment={(attachmentId) => void removeAttachment(attachmentId)}
          canAdjust={canModify && data.canInspect}
          onRemoveFront={(front) => void removeFront(front)}
          // RN-645 — a troca é um lançamento: pede também quem confirma o dia.
          {...(canConfirm
            ? {
                onReplaceFront: (allocationId: string) => {
                  const linha = linhasDoDia.find(
                    (item) => item.resource.resourceId === measuring?.resourceId,
                  );
                  const frente = linha?.allocations.find(
                    (item) => item.allocation.id === allocationId,
                  );
                  if (linha === undefined || frente === undefined) return;
                  const { activity, allocation, service } = frente;
                  setTroca({
                    date: date.toString(),
                    allocationId: allocation.id,
                    expectedVersion: allocation.version,
                    resourceId: linha.resource.resourceId,
                    resourceName: linha.resource.name,
                    activityId: activity.activityId,
                    ...(service === undefined ? {} : { serviceId: service.id }),
                    label:
                      service === undefined
                        ? activity.label
                        : `${activity.label} · ${service.name}`,
                    quarterHours: allocation.quarterHours,
                    ...(allocation.executedQuantity === undefined
                      ? {}
                      : { executedQuantity: allocation.executedQuantity }),
                  });
                  setMeasuring(undefined);
                },
              }
            : {})}
          canConfirm={canConfirm && data.canInspect}
          pieces={{ date: date.toString(), load: carregarPecas }}
          onHistory={() => {
            const linha = data.lines.find(
              (item) => item.resource.resourceId === measuring?.resourceId,
            );
            if (linha !== undefined) {
              setHistory({
                resourceId: linha.resource.resourceId,
                resourceName: linha.resource.name,
              });
            }
          }}
          onSave={(measurements, verifications, extras) => {
            if (measuring === undefined) return;
            void saveMeasurement({
              inspectionId: inspecaoDaCaixa ?? measuring.inspectionId,
              measurements,
              verifications,
              adjustments: extras?.adjustments ?? [],
              pieces: extras?.pieces ?? [],
              photos: extras?.photos ?? [],
              resourceId: measuring.resourceId,
              confirm: extras?.confirm ?? false,
              ...(extras?.justification === undefined
                ? {}
                : { justification: extras.justification }),
            });
          }}
        />

        <TeamMeasurementDialog
          open={measuringTeam !== undefined}
          onOpenChange={(open) => {
            if (!open) setMeasuringTeam(undefined);
          }}
          team={equipeMedida}
          front={frenteMedida}
          dateLabel={data.dateLabel}
          quality={quality !== undefined && quality.ok ? quality.value : undefined}
          nonConformityPhotos={{
            of: (id) =>
              galeriaComAFila(ncPhotos?.get(id), fila ?? [], { kind: 'nc', nonConformityId: id }),
            canAttach: canConfirm && data.canInspect,
            canRemove: canModify,
            isPending: attachNcState.isPending,
            resolveUrl: resolveAttachmentUrl,
            onAttach: (nonConformityId, files) => void attachNcPhotos({ nonConformityId, files }),
            onRemove: (attachmentId) => void removeAttachment(attachmentId),
          }}
          photos={
            frenteMedida?.measurement === undefined || measuringTeam === undefined ? (
              <p className="text-sm text-(--color-content-muted)">
                As fotos entram depois de salvar a medição.
              </p>
            ) : (
              <AttachmentGallery
                {...galeriaComAFila(
                  teamGallery !== undefined && teamGallery.ok ? teamGallery.value : undefined,
                  fila ?? [],
                  {
                    kind: 'equipe',
                    ...measuringTeam,
                    ...(medicaoDaEquipeId === undefined
                      ? {}
                      : { teamMeasurementId: medicaoDaEquipeId }),
                  },
                )}
                caption={{
                  value: legendaFotoEquipe,
                  onChange: setLegendaFotoEquipe,
                  required: false,
                }}
                canAttach={canConfirm && data.canInspect}
                canRemove={canModify}
                isPending={attachTeamState.isPending}
                {...(attachTeamState.error === undefined
                  ? {}
                  : { errorMessage: attachTeamState.error.message })}
                resolveUrl={resolveAttachmentUrl}
                onAttach={(files) =>
                  void attachTeamPhotos({
                    ...measuringTeam,
                    teamMeasurementId: medicaoDaEquipeId,
                    files,
                    caption: legendaFotoEquipe,
                  })
                }
                onRemove={(attachmentId) => void removeAttachment(attachmentId)}
              />
            )
          }
          pieces={{ date: date.toString(), load: carregarPecas }}
          isSaving={teamMeasurementState.isPending}
          errorMessage={teamMeasurementState.error?.message}
          onConfirm={({ quantity, verifications, pieceNames, previousPieceNames, photos }) => {
            if (measuringTeam === undefined) return;
            void saveTeamMeasurement({
              ...measuringTeam,
              quantity,
              verifications,
              ...(pieceNames === undefined ? {} : { pieceNames }),
              ...(previousPieceNames === undefined ? {} : { previousPieceNames }),
              ...(photos === undefined ? {} : { photos }),
            });
          }}
        />

        <ChangeHistoryDialog
          open={history !== undefined}
          onOpenChange={(open) => {
            if (!open) setHistory(undefined);
          }}
          resourceName={history?.resourceName ?? ''}
          dateLabel={data.dateLabel}
          changes={changes !== undefined && changes.ok ? changes.value : []}
          userNameOf={userNameOf}
        />
      </div>

      {/* O papel sai igual à tela: quem filtrou "pendentes" quer a lista do que falta. */}
      <InspectionReport
        dateLabel={data.dateLabel}
        projectName={project !== undefined && project.ok ? project.value.name : undefined}
        lines={lines}
      />
    </NcPlacesContext.Provider>
  );
}

/** "segunda-feira, 26/10/2026" — o rótulo que o servidor dá ao dia, para o dia que ele não deu. */
function rotuloDoDia(date: string): string {
  return new Intl.DateTimeFormat('pt-BR', {
    weekday: 'long',
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    timeZone: 'UTC',
  }).format(new Date(`${date}T12:00:00Z`));
}

/**
 * A barra do dia: as setas, a data e o "Hoje". Mesma barra da Programação do dia: a data toma a
 * linha, as setas têm 44px. Fica também no dia que não está no aparelho, sem sinal (RN-638).
 */
function BarraDoDia({
  dateLabel,
  isToday,
  onAnterior,
  onProximo,
  onHoje,
  children,
}: {
  dateLabel: string;
  isToday: boolean;
  onAnterior: () => void;
  onProximo: () => void;
  onHoje: () => void;
  children?: ReactNode;
}) {
  return (
    <div className="flex flex-wrap items-center gap-2 md:gap-3">
      <div className="flex w-full items-center gap-1 md:w-auto">
        <Button
          variant="ghost"
          size="icon"
          className="size-11 shrink-0 md:size-9"
          aria-label="Dia anterior"
          onClick={onAnterior}
        >
          <ChevronLeft aria-hidden="true" />
        </Button>
        <span className="min-w-0 flex-1 text-center text-sm font-medium md:min-w-64 md:text-base">
          {dateLabel}
        </span>
        <Button
          variant="ghost"
          size="icon"
          className="size-11 shrink-0 md:size-9"
          aria-label="Próximo dia"
          disabled={isToday}
          onClick={onProximo}
        >
          <ChevronRight aria-hidden="true" />
        </Button>
      </div>

      {!isToday && (
        <Button variant="ghost" size="sm" onClick={onHoje}>
          Hoje
        </Button>
      )}

      {children}
    </div>
  );
}
